import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BarChart3, Clock, FileText, ClipboardList, Stethoscope, 
  FileSignature, AlertCircle, TrendingUp, ArrowLeft, Zap,
  Calendar, Activity, ArrowUpRight, ArrowDownRight, Minus, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from './AuthProvider';
import { BrandEmblem } from './BrandEmblem';

const API = window.__WR_API_BASE__;

/* ─── Feature labels for dashboard display ─── */
const FEATURE_LABELS = {
  generate_docs: 'Generate Documents',
  speed_chart: 'Speed Chart',
  history_panel: 'History Panel',
  mood_changer: 'Mood / Theme Switcher',
  auto_share_notes: 'Auto-Share Notes',
  draft_streaming: 'Draft Streaming',
  voice_dictation: 'Voice Dictation',
  icd10_reorder: 'ICD-10 Reorder',
  quick_export_today: 'Quick Export Today',
  bulk_export: 'Bulk History Export',
  team_board: 'Team Board',
};

/* ─── Animated Counter ─── */
const AnimatedNumber = ({ value, suffix = '', duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (num === 0) { setDisplay(0); return; }
    let start = 0;
    const step = Math.max(1, Math.ceil(num / (duration / 16)));
    const tick = () => {
      start = Math.min(start + step, num);
      setDisplay(start);
      if (start < num) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => ref.current && cancelAnimationFrame(ref.current);
  }, [value, duration]);

  return <>{display.toLocaleString()}{suffix}</>;
};

/* ─── Color palette ─── */
const DOC_COLORS = {
  'Clinical Notes': '#3b82f6',
  'Imaging Orders': '#a855f7',
  'Referrals': '#22c55e',
  'Admin Letters': '#f59e0b',
  'Profiles': '#ef4444',
  'Limitations': '#f97316',
};

const DOC_ICONS = {
  'Clinical Notes': FileText,
  'Imaging Orders': Activity,
  'Referrals': ClipboardList,
  'Admin Letters': FileSignature,
  'Profiles': Stethoscope,
  'Limitations': AlertCircle,
};

/* ─── Stat Card with click-to-expand ─── */
const StatCard = ({ icon: Icon, label, value, suffix = '', color, delay = 0, detail, onClick, active }) => (
  <button 
    onClick={onClick}
    className={`relative flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border transition-all duration-300 hover:scale-[1.03] text-left w-full ${
      active ? 'ring-2 ring-primary/50 border-primary/40 bg-primary/5' : 'border-border bg-card hover:border-primary/30'
    }`}
    style={{ animationDelay: `${delay}ms`, animation: 'dashFadeSlideUp 0.5s ease both' }}
    data-testid={`stat-card-${label.toLowerCase().replace(/ /g, '-')}`}
  >
    <div className="p-2.5 rounded-xl" style={{ background: `${color}18` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <span className="text-3xl font-black tracking-tight text-foreground tabular-nums">
      <AnimatedNumber value={typeof value === 'number' ? value : parseFloat(value) || 0} suffix={suffix} />
    </span>
    <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider text-center leading-tight">{label}</span>
    {detail && <span className="text-[9px] text-muted-foreground/60">{detail}</span>}
    {active && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-primary" />}
  </button>
);

/* ─── CSS Donut Chart ─── */
const CSSDonut = ({ data, size = 180 }) => {
  const [hovered, setHovered] = useState(null);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  // Build conic-gradient segments
  let cumPct = 0;
  const segments = [];
  const slices = [];
  data.filter(d => d.value > 0).forEach(d => {
    const pct = (d.value / total) * 100;
    segments.push(`${d.color} ${cumPct}% ${cumPct + pct}%`);
    slices.push({ ...d, startPct: cumPct, endPct: cumPct + pct, pct });
    cumPct += pct;
  });

  const gradient = `conic-gradient(${segments.join(', ')})`;
  const r = size / 2;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Donut */}
      <div 
        className="absolute inset-0 rounded-full transition-transform duration-300"
        style={{ 
          background: gradient, 
          mask: `radial-gradient(circle at center, transparent ${r * 0.58}px, black ${r * 0.58 + 1}px)`,
          WebkitMask: `radial-gradient(circle at center, transparent ${r * 0.58}px, black ${r * 0.58 + 1}px)`,
        }} 
      />
      {/* Invisible hover arcs */}
      <svg className="absolute inset-0" viewBox={`0 0 ${size} ${size}`} style={{ zIndex: 2 }}>
        {slices.map((s, i) => {
          const startAngle = (s.startPct / 100) * 360 - 90;
          const endAngle = (s.endPct / 100) * 360 - 90;
          const midAngle = (startAngle + endAngle) / 2;
          const innerR = r * 0.58;
          const outerR = r;
          const toRad = (deg) => (deg * Math.PI) / 180;
          const path = [
            `M ${r + innerR * Math.cos(toRad(startAngle))} ${r + innerR * Math.sin(toRad(startAngle))}`,
            `L ${r + outerR * Math.cos(toRad(startAngle))} ${r + outerR * Math.sin(toRad(startAngle))}`,
            `A ${outerR} ${outerR} 0 ${s.pct > 50 ? 1 : 0} 1 ${r + outerR * Math.cos(toRad(endAngle))} ${r + outerR * Math.sin(toRad(endAngle))}`,
            `L ${r + innerR * Math.cos(toRad(endAngle))} ${r + innerR * Math.sin(toRad(endAngle))}`,
            `A ${innerR} ${innerR} 0 ${s.pct > 50 ? 1 : 0} 0 ${r + innerR * Math.cos(toRad(startAngle))} ${r + innerR * Math.sin(toRad(startAngle))}`,
          ].join(' ');
          return (
            <path 
              key={i} 
              d={path} 
              fill="transparent" 
              className="cursor-pointer"
              onMouseEnter={() => setHovered(s.name)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>
      {/* Center text */}
      <div className="flex flex-col items-center z-10 pointer-events-none">
        {hovered ? (
          <>
            <span className="text-lg font-black text-foreground tabular-nums">
              {data.find(d => d.name === hovered)?.value || 0}
            </span>
            <span className="text-[8px] text-muted-foreground uppercase tracking-wider max-w-[80px] text-center leading-tight">
              {hovered}
            </span>
          </>
        ) : (
          <>
            <span className="text-2xl font-black text-foreground tabular-nums">{total}</span>
            <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Total</span>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Donut + Legend Section ─── */
const DonutBreakdown = ({ title, data, period }) => {
  const total = data.reduce((s, d) => s + d.value, 0);

  if (total === 0) {
    return (
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-8">
          <FileText className="w-10 h-10 text-muted-foreground/20 mb-2" />
          <p className="text-xs text-muted-foreground">No documents {period}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border overflow-hidden" data-testid={`donut-${title.toLowerCase().replace(/ /g, '-')}`}>
      <CardHeader className="pb-1 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-foreground">{title}</CardTitle>
        <Badge variant="secondary" className="text-xs font-bold tabular-nums">{total} docs</Badge>
      </CardHeader>
      <CardContent className="flex flex-col items-center pb-4">
        <CSSDonut data={data} size={180} />
        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 justify-center mt-4">
          {data.filter(d => d.value > 0).map(d => (
            <div key={d.name} className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
              <span className="text-muted-foreground">{d.name}</span>
              <span className="font-bold text-foreground tabular-nums">{d.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── Trend Badge ─── */
const TrendBadge = ({ current, previous, label }) => {
  const diff = current - previous;
  const pct = previous > 0 ? Math.round((diff / previous) * 100) : (current > 0 ? 100 : 0);
  const isUp = diff > 0;
  const isFlat = diff === 0;

  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold tabular-nums text-foreground">{current}</span>
        {!isFlat && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
            isUp ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-400'
          }`}>
            {isUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(pct)}%
          </span>
        )}
        {isFlat && (
          <span className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground px-1.5 py-0.5 rounded-full bg-secondary/60">
            <Minus className="w-3 h-3" /> 0%
          </span>
        )}
      </div>
    </div>
  );
};

/* ─── Weekly Recap ─── */
const WeeklyRecap = ({ thisWeek, lastWeek, docItems, docKey }) => {
  const twSessions = thisWeek.total_sessions || 0;
  const lwSessions = lastWeek.total_sessions || 0;
  const twHours = thisWeek.time_saved_hours || 0;
  const lwHours = lastWeek.time_saved_hours || 0;
  const twDocs = docItems.reduce((s, d) => s + (thisWeek[docKey(d.label)] || 0), 0);
  const lwDocs = docItems.reduce((s, d) => s + (lastWeek[docKey(d.label)] || 0), 0);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const dayOfWeek = dayNames[today.getDay()];

  return (
    <Card className="bg-card border-border overflow-hidden" data-testid="weekly-recap-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Calendar className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Last 7 Days</CardTitle>
            <p className="text-xs text-muted-foreground">{dayOfWeek} &middot; Rolling 7-day window &middot; vs. prior 7</p>
          </div>
        </div>
        {twSessions > lwSessions && (
          <span className="text-xs font-bold text-emerald-500 flex items-center gap-1 bg-emerald-500/10 px-2 py-1 rounded-full">
            <TrendingUp className="w-3.5 h-3.5" /> Up
          </span>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="divide-y divide-border/50">
          <TrendBadge current={twSessions} previous={lwSessions} label="Patients Charted" />
          <TrendBadge current={twDocs} previous={lwDocs} label="Docs Knocked Out" />
          <TrendBadge current={twHours} previous={lwHours} label="Hours Back" />
        </div>
        {twSessions === 0 && (
          <p className="text-xs text-muted-foreground/70 text-center pt-3 italic">No sessions this week yet — chart one and watch it add up.</p>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── Expanded Detail Panel ─── */
const DetailPanel = ({ statKey, stats, docItems, docKey, onClose }) => {
  const titles = {
    'hours_month': 'Hours Reclaimed This Month',
    'hours_all': 'Total Hours Reclaimed',
    'patients_month': 'Patients Seen This Month',
    'one_stop': 'One-Stop Sessions (All Time)',
  };

  const descriptions = {
    'hours_month': 'Time you saved by generating docs instead of hand-writing them this month.',
    'hours_all': 'Cumulative time saved across all your charting sessions.',
    'patients_month': 'Unique charting sessions with at least one document generated.',
    'one_stop': 'Sessions where you generated 2+ document types at once — the most efficient workflow.',
  };

  const allTime = stats?.all_time || {};
  const thisMonth = stats?.this_month || {};
  const period = statKey.includes('month') ? thisMonth : allTime;
  const breakdown = docItems.map(d => ({
    name: d.label,
    value: period[docKey(d.label)] || 0,
    color: DOC_COLORS[d.label],
    icon: DOC_ICONS[d.label],
  })).filter(d => d.value > 0);

  const total = breakdown.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="bg-card border-primary/30 ring-1 ring-primary/20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300" data-testid="detail-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm font-semibold">{titles[statKey] || 'Details'}</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">{descriptions[statKey]}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" data-testid="close-detail-panel"><X className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        {breakdown.length > 0 ? (
          <div className="space-y-2">
            {breakdown.map(d => {
              const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
              const Icon = d.icon;
              return (
                <div key={d.name} className="flex items-center gap-3 py-1.5">
                  <div className="p-1.5 rounded-lg shrink-0" style={{ background: `${d.color}18` }}>
                    <Icon className="w-4 h-4" style={{ color: d.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-foreground">{d.name}</span>
                      <span className="text-xs font-bold tabular-nums text-foreground">{d.value} <span className="text-muted-foreground font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct}%`, background: d.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">No documents generated yet for this period.</p>
        )}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════ */
const ProviderDashboard = ({ user: propUser }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const user = propUser || authUser;
  const [stats, setStats] = useState(null);
  const [featureUsage, setFeatureUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStat, setExpandedStat] = useState(null);
  const isEmbedded = !!propUser;

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) { setLoading(false); return; }
      try {
        const [statsRes, featRes] = await Promise.all([
          axios.get(`${API}/api/stats/provider`, { withCredentials: true }),
          axios.get(`${API}/api/stats/feature-usage`, { withCredentials: true }).catch(() => ({ data: { features: [] } })),
        ]);
        setStats(statsRes.data);
        setFeatureUsage(featRes.data?.features || []);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setError(err.response?.data?.detail || 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold">Sign In Required</h2>
            <p className="text-muted-foreground">Please sign in to view your productivity dashboard.</p>
            <Button onClick={() => navigate('/')} data-testid="go-home-btn">Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`${isEmbedded ? '' : 'min-h-screen'} bg-background flex items-center justify-center py-12`}>
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-sm text-muted-foreground">Loading stats...</span>
        </div>
      </div>
    );
  }

  const allTime = stats?.all_time || {};
  const thisMonth = stats?.this_month || {};
  const thisWeek = stats?.this_week || {};
  const lastWeek = stats?.last_week || {};

  const docItems = [
    { label: 'Clinical Notes', icon: FileText, color: '#3b82f6' },
    { label: 'Imaging Orders', icon: Activity, color: '#a855f7' },
    { label: 'Referrals', icon: ClipboardList, color: '#22c55e' },
    { label: 'Admin Letters', icon: FileSignature, color: '#f59e0b' },
    { label: 'Profiles', icon: Stethoscope, color: '#ef4444' },
    { label: 'Limitations', icon: AlertCircle, color: '#f97316' },
  ];

  const docKey = (label) => label.toLowerCase().replace(/ /g, '_');
  const makeChartData = (period) => docItems.map(d => ({
    name: d.label,
    value: period[docKey(d.label)] || 0,
    color: d.color,
  }));

  const toggleStat = (key) => setExpandedStat(prev => prev === key ? null : key);

  const content = (
    <>
      <style>{`
        @keyframes dashFadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {error ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-5 text-destructive">{error}</CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {/* ── Hero Stat Row ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard 
              icon={Clock} label="Hours Back (Month)" 
              value={thisMonth.time_saved_hours || 0} suffix="h" 
              color="#6366f1" delay={0}
              onClick={() => toggleStat('hours_month')}
              active={expandedStat === 'hours_month'}
            />
            <StatCard 
              icon={TrendingUp} label="Total Hours Reclaimed" 
              value={allTime.time_saved_hours || 0} suffix="h" 
              color="#22c55e" delay={80}
              onClick={() => toggleStat('hours_all')}
              active={expandedStat === 'hours_all'}
            />
            <StatCard 
              icon={Calendar} label="Patients (Month)" 
              value={thisMonth.total_sessions || 0} 
              color="#f59e0b" delay={160}
              onClick={() => toggleStat('patients_month')}
              active={expandedStat === 'patients_month'}
            />
            <StatCard 
              icon={Zap} label="One-Stop Sessions" 
              value={allTime.multi_doc_sessions || 0} 
              color="#ec4899" delay={240}
              detail={allTime.total_sessions > 0 ? `${Math.round((allTime.multi_doc_sessions / allTime.total_sessions) * 100)}% efficiency` : null}
              onClick={() => toggleStat('one_stop')}
              active={expandedStat === 'one_stop'}
            />
          </div>

          {/* ── Expanded Detail Panel ── */}
          {expandedStat && (
            <DetailPanel 
              statKey={expandedStat}
              stats={stats}
              docItems={docItems}
              docKey={docKey}
              onClose={() => setExpandedStat(null)}
            />
          )}

          {/* ── Weekly Recap ── */}
          <WeeklyRecap thisWeek={thisWeek} lastWeek={lastWeek} docItems={docItems} docKey={docKey} />

          {/* ── Efficiency Banner ── */}
          <div 
            className="flex items-center justify-between rounded-xl px-5 py-4 border"
            style={{ borderColor: 'var(--border)', background: 'linear-gradient(135deg, var(--card) 0%, var(--secondary) 100%)' }}
            data-testid="efficiency-banner"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-foreground font-semibold">Saturdays Earned</p>
                <p className="text-xs text-muted-foreground">% of visits where you knocked out 2+ doc types at once</p>
              </div>
            </div>
            <span className="text-3xl font-black text-primary tabular-nums" data-testid="efficiency-rate">
              {allTime.total_sessions > 0 ? Math.round((allTime.multi_doc_sessions / allTime.total_sessions) * 100) : 0}%
            </span>
          </div>

          {/* ── Donut Charts ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <DonutBreakdown title="This Month" data={makeChartData(thisMonth)} period="this month" />
            <DonutBreakdown title="All Time" data={makeChartData(allTime)} period="yet" />
          </div>

          {/* ── Pragmatic Accountability: Drafts + Unsubmitted + Submitted ── */}
          {((allTime.draft_count || 0) > 0 || (allTime.unsubmitted_count || 0) > 0 || (allTime.submitted_count || 0) > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="accountability-board">
              {(allTime.submitted_count || 0) > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10" data-testid="submitted-indicator">
                  <div className="p-2 rounded-lg bg-emerald-500/20">
                    <FileText className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xl font-black text-emerald-400 tabular-nums leading-none">{allTime.submitted_count}</p>
                    <p className="text-[11px] text-emerald-300/80 mt-0.5">Submitted to Genesis</p>
                  </div>
                </div>
              )}
              {(allTime.unsubmitted_count || 0) > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/40 bg-amber-500/10" data-testid="unsubmitted-indicator">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <FileText className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xl font-black text-amber-400 tabular-nums leading-none">{allTime.unsubmitted_count}</p>
                    <p className="text-[11px] text-amber-300/80 mt-0.5">Needs submitting</p>
                  </div>
                </div>
              )}
              {(allTime.draft_count || 0) > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-secondary/20" data-testid="draft-indicator">
                  <div className="p-2 rounded-lg bg-muted-foreground/10">
                    <FileText className="w-4 h-4 text-muted-foreground/60" />
                  </div>
                  <div>
                    <p className="text-xl font-black text-muted-foreground tabular-nums leading-none">{allTime.draft_count}</p>
                    <p className="text-[11px] text-muted-foreground/80 mt-0.5">Drafts in progress</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Your Top Features ── */}
          {featureUsage.length > 0 && (
            <Card className="bg-card border-border" data-testid="top-features-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-violet-500/10">
                    <Zap className="w-5 h-5 text-violet-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold">Your Power Moves</CardTitle>
                    <p className="text-xs text-muted-foreground">Features you use most — tweak in Settings</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {featureUsage.slice(0, 5).map((f, i) => {
                    const maxCount = featureUsage[0]?.count || 1;
                    const pct = Math.round((f.count / maxCount) * 100);
                    const label = FEATURE_LABELS[f.feature] || f.feature.replace(/_/g, ' ');
                    return (
                      <div key={f.feature} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-muted-foreground/70 w-5 text-right tabular-nums">#{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm font-medium text-foreground truncate">{label}</span>
                            <span className="text-xs font-bold tabular-nums text-foreground ml-2">{f.count}x</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-secondary/60 overflow-hidden">
                            <div className="h-full rounded-full bg-violet-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Time Settings ── */}
          {stats?.time_settings && (
            <p className="text-[11px] text-muted-foreground/70 text-center px-4">
              Time estimates: SOAP {stats.time_settings.soap_note}min &middot; Imaging {stats.time_settings.imaging_order}min &middot; Referral {stats.time_settings.referral}min &middot; Letter {stats.time_settings.admin_letter}min &middot; Profile {stats.time_settings.profile}min
            </p>
          )}

          {/* ── Motivational Footer ── */}
          {(allTime.time_saved_hours || 0) > 0 && (
            <div className="text-center py-3 px-4 rounded-lg bg-card border border-border">
              <p className="text-sm text-muted-foreground italic">
                {allTime.time_saved_hours >= 40
                  ? "That's a full work week you didn't spend writing notes."
                  : allTime.time_saved_hours >= 8
                  ? "That's a whole duty day you got back — guilt free."
                  : allTime.time_saved_hours >= 2
                  ? `That's ${allTime.time_saved_hours} hours closer to getting home on time.`
                  : "Every hour counts. Keep going."
                }
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-app-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to App
          </Button>
          <div style={{ '--brand-text-color': 'var(--foreground)' }}>
            <BrandEmblem size="sm" />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-black text-foreground flex items-center gap-3" data-testid="dashboard-title">
            <BarChart3 className="w-7 h-7 text-primary" />
            Your Impact
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Time you've taken back from paperwork
          </p>
        </div>
        {content}
      </main>
    </div>
  );
};

export default ProviderDashboard;
